import React, { useEffect, useState } from "react";
import { Save, RotateCcw, FileJson, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { notify } from "../../lib/notify";

export function ContentBlocksAdmin() {
  const [blocks, setBlocks] = useState([]);
  const [editing, setEditing] = useState(null); // {key, label, source}
  const [text, setText] = useState("");
  const [jsonErr, setJsonErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/content-blocks").then(r => setBlocks(r.data.blocks || []));
  useEffect(() => { load(); }, []);

  const openBlock = async (key) => {
    try {
      const r = await api.get(`/admin/content-blocks/${key}`);
      setEditing(r.data);
      setText(JSON.stringify(r.data.data, null, 2));
      setJsonErr(null);
    } catch { notify.error("Could not load block."); }
  };

  const onTextChange = (v) => {
    setText(v);
    try { JSON.parse(v); setJsonErr(null); }
    catch (e) { setJsonErr(e.message); }
  };

  const save = async () => {
    let data;
    try { data = JSON.parse(text); }
    catch (e) { setJsonErr(e.message); notify.error("Invalid JSON — fix before saving."); return; }
    setBusy(true);
    try {
      await api.put(`/admin/content-blocks/${editing.key}`, { data });
      notify.success(`"${editing.label}" saved. Live immediately.`);
      setEditing(null);
      load();
    } catch (e) { notify.error(e.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset "${editing.label}" to the built-in default? Your customisations will be discarded.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/content-blocks/${editing.key}`);
      notify.success("Reset to default.");
      setEditing(null);
      load();
    } catch { notify.error("Reset failed."); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="max-w-4xl" data-testid="content-block-editor">
        <div className="overline flex items-center gap-2"><FileJson size={11} /> {editing.kind === "list" ? "JSON array" : "JSON object"} · {editing.source}</div>
        <h2 className="font-display text-3xl font-semibold mt-3">{editing.label}</h2>
        <p className="text-slate-600 mt-2 text-sm">Edit the JSON below. Changes go live on all wizards / pages immediately after saving.</p>
        <textarea
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          spellCheck={false}
          className="w-full mt-6 border rounded-sm p-4 font-mono text-xs leading-relaxed min-h-[480px]"
          style={{ borderColor: jsonErr ? "#e11d48" : "var(--rp-border)", background: "var(--rp-bg-subtle)" }}
          data-testid="content-block-textarea"
        />
        {jsonErr && <div className="text-xs text-rose-600 mt-2 font-mono" data-testid="content-block-json-error">JSON error: {jsonErr}</div>}
        <div className="flex flex-wrap gap-3 mt-4">
          <button onClick={save} disabled={busy || !!jsonErr} className="btn-accent disabled:opacity-50" data-testid="content-block-save"><Save size={14} /> Save</button>
          {editing.source === "custom" && (
            <button onClick={reset} disabled={busy} className="btn-outline text-rose-600" data-testid="content-block-reset"><RotateCcw size={14} /> Reset to default</button>
          )}
          <button onClick={() => setEditing(null)} className="btn-outline" data-testid="content-block-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  const groups = [...new Set(blocks.map(b => b.group))];
  return (
    <div className="max-w-4xl" data-testid="content-blocks-admin">
      <div className="overline flex items-center gap-2"><FileJson size={11} /> Wizards & deep-dives content</div>
      <h2 className="font-display text-3xl font-semibold mt-3">Content blocks.</h2>
      <p className="text-slate-600 mt-3 leading-relaxed">Every wizard question set, penalty regime, deadline and money-flow deep-dive is editable here — no code deploy needed. Blocks marked <strong>custom</strong> override the built-in defaults.</p>
      {groups.map(g => (
        <div key={g} className="mt-8">
          <div className="overline-muted">{g}</div>
          <div className="mt-3 border border-slate-200 divide-y divide-slate-100 rounded-sm">
            {blocks.filter(b => b.group === g).map(b => (
              <button key={b.key} onClick={() => openBlock(b.key)} className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50" data-testid={`content-block-${b.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm font-semibold">{b.label}</div>
                  <div className="text-[10px] font-mono text-slate-400 mt-0.5">{b.key} · {b.items_count} {b.kind === "list" ? "items" : "keys"}{b.updated_at ? ` · edited ${new Date(b.updated_at).toLocaleDateString()}` : ""}</div>
                </div>
                {b.source === "custom"
                  ? <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm text-white inline-flex items-center gap-1" style={{ background: "var(--rp-accent)" }}><CheckCircle2 size={9} /> custom</span>
                  : <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "var(--rp-bg-subtle)", border: "1px solid var(--rp-border)" }}>default</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
